import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import styles from "./DashboardPage.module.css";

export default function DashboardPage() {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // User stats
  const [totalUsers, setTotalUsers] = useState(0);
  const [todayLogins, setTodayLogins] = useState(0);
  const [todayNewUsers, setTodayNewUsers] = useState(0);
  const [activeUsers7d, setActiveUsers7d] = useState(0);

  // Content stats
  const [wordsCount, setWordsCount] = useState(0);
  const [pairsCount, setPairsCount] = useState(0);
  const [quizzesCount, setQuizzesCount] = useState(0);
  const [scenariosCount, setScenariosCount] = useState(0);
  const [nuanceGroupsCount, setNuanceGroupsCount] = useState(0);

  // Daily stats
  const [todayStudyTotal, setTodayStudyTotal] = useState(0);
  const [todayStudyCorrect, setTodayStudyCorrect] = useState(0);
  const [todayLearners, setTodayLearners] = useState(0);
  const [totalStudyRecords, setTotalStudyRecords] = useState(0);

  // Panels
  const [recentLogins, setRecentLogins] = useState<{email:string,device:string,logged_in_at:string}[]>([]);
  const [notices, setNotices] = useState<{id:string,title:string,created_at:string,is_pinned:boolean}[]>([]);
  const [myTodos, setMyTodos] = useState<{id:string,title:string,status:string,due_date:string|null,user_id:string}[]>([]);
  const [allTodos, setAllTodos] = useState<{id:string,title:string,status:string,due_date:string|null,user_id:string}[]>([]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setCurrentUser(data.user);
      fetchAll(data.user);
    });
  }, []);

  async function fetchAll(user: any) {
    setLoading(true);
    const todayStart = new Date(); todayStart.setHours(0,0,0,0);
    const sevenDaysAgo = new Date(); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const todayStr = todayStart.toISOString().split('T')[0];
    const [
      { count: uCount },
      { count: loginToday },
      { count: newToday },
      { data: recentLoginsData },
      { data: activeData },
      { count: wCount },
      { count: wpCount },
      { count: qCount },
      { count: scCount },
      { count: ngCount },
      { data: noticesData },
      { data: todayStatsData },
      { count: srCount },
      { data: todosData },
      { data: allTodosData },
    ] = await Promise.all([
      supabase.from("users").select("*", { count: "exact", head: true }),
      supabase.from("login_logs").select("*", { count: "exact", head: true }).gte("logged_in_at", todayStart.toISOString()),
      supabase.from("users").select("*", { count: "exact", head: true }).gte("created_at", todayStart.toISOString()),
      supabase.from("login_logs").select("user_id, email:user_id, device, logged_in_at").order("logged_in_at", { ascending: false }).limit(5),
      supabase.from("login_logs").select("user_id").gte("logged_in_at", sevenDaysAgo.toISOString()),
      supabase.from("words").select("*", { count: "exact", head: true }),
      supabase.from("word_pairs").select("*", { count: "exact", head: true }),
      supabase.from("concept1_quizzes").select("*", { count: "exact", head: true }),
      supabase.from("scenarios").select("*", { count: "exact", head: true }),
      supabase.from("nuance_groups").select("*", { count: "exact", head: true }),
      supabase.from("notices").select("id, title, created_at, is_pinned").order("is_pinned", { ascending: false }).order("created_at", { ascending: false }).limit(4),
      supabase.from("daily_stats").select("user_id, total_count, correct_count").eq("date", todayStr),
      supabase.from("study_records").select("*", { count: "exact", head: true }),
      user ? supabase.from("todos").select("id, title, status, due_date, user_id").eq("user_id", user.id).neq("status", "done").order("due_date", { ascending: true, nullsFirst: false }).limit(5) : Promise.resolve({ data: [] }),
      supabase.from("todos").select("id, title, status, due_date, user_id").neq("status", "done").order("due_date", { ascending: true, nullsFirst: false }).limit(20),
    ]);

    setTotalUsers(uCount ?? 0);
    setTodayLogins(loginToday ?? 0);
    setTodayNewUsers(newToday ?? 0);

    // Distinct active users in 7d
    const uniqueUsers = new Set((activeData ?? []).map((r: any) => r.user_id));
    setActiveUsers7d(uniqueUsers.size);

    // Resolve emails for recent logins
    const logRows = recentLoginsData ?? [];
    const userIds = Array.from(new Set(logRows.map((r: any) => r.user_id).filter(Boolean)));
    let emailMap: Record<string, string> = {};
    if (userIds.length > 0) {
      const { data: uData } = await supabase.from("users").select("id, email").in("id", userIds);
      (uData ?? []).forEach((u: any) => { emailMap[u.id] = u.email; });
    }
    setRecentLogins(logRows.map((r: any) => ({ email: emailMap[r.user_id] ?? r.user_id ?? "—", device: r.device ?? "—", logged_in_at: r.logged_in_at })));

    // Daily study stats
    const statsRows = todayStatsData ?? [];
    const totalQ = statsRows.reduce((s: number, r: any) => s + (r.total_count ?? 0), 0);
    const correctQ = statsRows.reduce((s: number, r: any) => s + (r.correct_count ?? 0), 0);
    setTodayStudyTotal(totalQ);
    setTodayStudyCorrect(correctQ);
    setTodayLearners(statsRows.length);
    setTotalStudyRecords(srCount ?? 0);

    setWordsCount(wCount ?? 0);
    setPairsCount(wpCount ?? 0);
    setQuizzesCount(qCount ?? 0);
    setScenariosCount(scCount ?? 0);
    setNuanceGroupsCount(ngCount ?? 0);
    setNotices(noticesData ?? []);
    setMyTodos(todosData ?? []);
    setAllTodos(allTodosData ?? []);
    setLoading(false);
  }

  const contentTotal = Math.max(wordsCount + pairsCount + quizzesCount + scenariosCount + nuanceGroupsCount, 1);

  const STATUS_LABEL: Record<string, string> = { todo: '할 일', in_progress: '진행 중', done: '완료' };
  const STATUS_COLOR: Record<string, string> = { todo: 'rgba(255,255,255,0.2)', in_progress: 'rgba(155,127,212,0.5)', done: 'rgba(100,200,150,0.4)' };

  function timeAgo(iso: string) {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}초 전`;
    if (diff < 3600) return `${Math.floor(diff/60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff/3600)}시간 전`;
    return `${Math.floor(diff/86400)}일 전`;
  }

  if (loading) return <div className={styles.loading}>로딩 중...</div>;

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1 className={styles.title}>대시보드</h1>
          <p className={styles.subtitle}>Gang-Z 관리자 콘솔</p>
        </div>
        <button className="btn btn-ghost" style={{fontSize:13}} onClick={() => fetchAll(currentUser)}>↺ 새로고침</button>
      </div>

      {/* 유저 현황 */}
      <div className={styles.sectionLabel}>유저 현황</div>
      <div className={styles.statGrid}>
        <StatCard label="전체 가입자" value={totalUsers} unit="명" sub="+신규 오늘" subVal={todayNewUsers} />
        <StatCard label="오늘 접속자" value={todayLogins} unit="명" sub="로그인 기준" />
        <StatCard label="오늘 신규 가입" value={todayNewUsers} unit="명" />
        <StatCard label="활성 유저 (7일)" value={activeUsers7d} unit="명" sub="중복 제거" />
      </div>

      {/* 학습 현황 */}
      <div className={styles.sectionLabel}>오늘의 학습 현황</div>
      <div className={styles.statGrid}>
        <StatCard label="오늘 학습 문제" value={todayStudyTotal} unit="문제" sub="전체 누적" subVal={totalStudyRecords} />
        <StatCard label="오늘 정답 수" value={todayStudyCorrect} unit="개" sub={todayStudyTotal > 0 ? `정확도 ${Math.round((todayStudyCorrect/todayStudyTotal)*100)}%` : '정확도 —'} />
        <StatCard label="오늘 학습자" value={todayLearners} unit="명" sub="daily_stats 기준" />
      </div>

      {/* 콘텐츠 현황 */}
      <div className={styles.sectionLabel}>콘텐츠 현황</div>
      <div className={styles.statGrid}>
        <StatCard label="전체 단어" value={wordsCount} unit="개" />
        <StatCard label="단어 쌍" value={pairsCount} unit="개" />
        <StatCard label="Concept 1 퀴즈" value={quizzesCount} unit="개" />
        <StatCard label="시나리오" value={scenariosCount} unit="개" />
        <StatCard label="뉘앙스 그룹" value={nuanceGroupsCount} unit="개" />
      </div>

      {/* Middle panels */}
      <div className={styles.panels}>
        {/* 공지사항 */}
        <div className={styles.panel}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
            <div className={styles.panelTitle} style={{marginBottom:0}}>공지사항</div>
            <a href="/v1/notices" style={{fontSize:11,color:'rgba(255,255,255,0.4)',textDecoration:'none'}}>전체 보기 →</a>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            {notices.length === 0 ? (
              <div style={{color:'rgba(255,255,255,0.3)',fontSize:13,padding:'20px 0'}}>공지 없음</div>
            ) : notices.map((n, i) => (
              <div key={n.id} style={{padding:'10px 0',borderBottom: i < notices.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none'}}>
                <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:3}}>
                  {n.is_pinned && <span style={{width:6,height:6,borderRadius:'50%',background:'#E8649A',flexShrink:0,display:'inline-block'}}/>}
                  <span style={{fontSize:13,fontWeight:600,color:'white',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{n.title}</span>
                </div>
                <div style={{fontSize:11,color:'rgba(255,255,255,0.35)'}}>
                  {new Date(n.created_at).toLocaleDateString('ko-KR',{month:'2-digit',day:'2-digit'})} · 관리자
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 최근 접속 유저 */}
        <div className={styles.panel}>
          <div className={styles.panelTitle}>최근 접속 유저</div>
          <div style={{display:'flex',flexDirection:'column',gap:2,marginTop:4}}>
            {recentLogins.length === 0 ? (
              <div style={{color:'rgba(255,255,255,0.3)',fontSize:13,padding:'20px 0'}}>접속 이력 없음</div>
            ) : recentLogins.map((r, i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'9px 0',borderBottom: i < recentLogins.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none'}}>
                <div style={{
                  width:34,height:34,borderRadius:'50%',
                  background:'rgba(155,127,212,0.3)',border:'1px solid rgba(155,127,212,0.4)',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:13,fontWeight:700,color:'white',flexShrink:0,
                }}>
                  {r.email.charAt(0).toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:'white',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.email}</div>
                  <div style={{fontSize:11,color:'rgba(255,255,255,0.4)',marginTop:1}}>{timeAgo(r.logged_in_at)}</div>
                </div>
                <span style={{fontSize:10,color:'rgba(255,255,255,0.35)',background:'rgba(255,255,255,0.08)',padding:'2px 7px',borderRadius:4,flexShrink:0}}>
                  {r.device}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 콘텐츠 비율 */}
        <div className={styles.panel}>
          <div className={styles.panelTitle}>콘텐츠 비율</div>
          <div style={{display:'flex',flexDirection:'column',gap:14,marginTop:4}}>
            {[
              {label:'단어',value:wordsCount,color:'#9B7FD4'},
              {label:'단어 쌍',value:pairsCount,color:'#7B6FA0'},
              {label:'퀴즈',value:quizzesCount,color:'#34C98A'},
              {label:'시나리오',value:scenariosCount,color:'#64C8FF'},
              {label:'뉘앙스 그룹',value:nuanceGroupsCount,color:'#F0C040'},
            ].map(item => (
              <div key={item.label}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                  <span style={{fontSize:12,color:'rgba(255,255,255,0.7)'}}>{item.label}</span>
                  <span style={{fontSize:12,fontWeight:700,color:'white'}}>{item.value}개</span>
                </div>
                <div style={{height:6,background:'rgba(255,255,255,0.1)',borderRadius:3,overflow:'hidden'}}>
                  <div style={{
                    height:'100%',borderRadius:3,
                    background:item.color,
                    width:`${Math.round((item.value/contentTotal)*100)}%`,
                    transition:'width 0.4s ease',
                  }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom panels */}
      <div className={styles.bottomPanels}>
        {/* 내 할 일 */}
        <div className={styles.panel}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
            <div className={styles.panelTitle} style={{marginBottom:0}}>내 할 일</div>
            <a href="/v1/todos" style={{fontSize:11,color:'rgba(255,255,255,0.4)',textDecoration:'none'}}>전체 보기 →</a>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            {myTodos.length === 0 ? (
              <div style={{color:'rgba(255,255,255,0.3)',fontSize:13,padding:'20px 0'}}>할 일 없음</div>
            ) : myTodos.map((t, i) => (
              <div key={t.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 0',borderBottom: i < myTodos.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none'}}>
                <span style={{
                  padding:'2px 9px',borderRadius:20,fontSize:11,fontWeight:700,flexShrink:0,
                  background:STATUS_COLOR[t.status] ?? 'rgba(255,255,255,0.1)',
                  color:'white',
                }}>{STATUS_LABEL[t.status] ?? t.status}</span>
                <span style={{flex:1,fontSize:13,color:'white',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.title}</span>
                {t.due_date && (
                  <span style={{fontSize:11,color:'rgba(255,255,255,0.35)',flexShrink:0}}>
                    ~{new Date(t.due_date).toLocaleDateString('ko-KR',{month:'2-digit',day:'2-digit'})}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 전체 할 일 */}
        <div className={styles.panel}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
            <div className={styles.panelTitle} style={{marginBottom:0}}>전체 할 일</div>
            <a href="/v1/todos" style={{fontSize:11,color:'rgba(255,255,255,0.4)',textDecoration:'none'}}>전체 보기 →</a>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            {allTodos.length === 0 ? (
              <div style={{color:'rgba(255,255,255,0.3)',fontSize:13,padding:'20px 0'}}>할 일 없음</div>
            ) : allTodos.map((t, i) => (
              <div key={t.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 0',borderBottom: i < allTodos.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none'}}>
                <span style={{
                  padding:'2px 9px',borderRadius:20,fontSize:11,fontWeight:700,flexShrink:0,
                  background:STATUS_COLOR[t.status] ?? 'rgba(255,255,255,0.1)',color:'white',
                }}>{STATUS_LABEL[t.status] ?? t.status}</span>
                <span style={{flex:1,fontSize:13,color: t.user_id === currentUser?.id ? 'white' : 'rgba(255,255,255,0.6)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.title}</span>
                {t.due_date && (
                  <span style={{fontSize:11,color:'rgba(255,255,255,0.35)',flexShrink:0}}>
                    ~{new Date(t.due_date).toLocaleDateString('ko-KR',{month:'2-digit',day:'2-digit'})}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, unit, sub, subVal }: {
  label: string; value: number; unit: string; sub?: string; subVal?: number;
}) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statLabel}>
        {label}
        {sub && <span className={styles.statSub}> ({subVal !== undefined ? `+${subVal} ` : ''}{sub})</span>}
      </div>
      <div className={styles.statValue}>{value.toLocaleString()}<span className={styles.statUnit}>{unit}</span></div>
    </div>
  );
}
